import { useState } from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { YoutubeIsEmpty, YoutubeLayout, YoutubeProps } from './youtube.config';
import useYoutubeThumbnail from './use-youtube-thumbnail';
import YoutubeEmbed from './youtube-embed/youtube-embed';

const YOUTUBE_URL = 'https://www.youtube.com';
const GOOGLE_URL = 'https://www.google.com';

export const Youtube = ({
  id,
  youtubeVideoId,
  layout,
  youtubeAspectRatio,
  accessibilityLabel,
  youtubeWidth,
  youtubeHeight,
  youtubeMute,
  youtubeLoop,
  appliedCssClassNames,
  isInEditor,
  cqPath,
}: YoutubeProps) => {
  const { t } = useTranslation();

  const [iframe, setIframe] = useState(false);
  const [preconnected, setPreconnected] = useState(false);

  const addIframe = () => {
    if (iframe) {
      return;
    }
    setIframe(true);
  };

  const warmConnections = () => {
    if (preconnected || iframe) {
      return;
    }
    setPreconnected(true);
  };

  const isResponsive = layout === YoutubeLayout.RESPONSIVE;

  const thumbnailUrl = useYoutubeThumbnail({
    videoId: youtubeVideoId ?? '',
    enabled: !iframe || isInEditor,
  });

  const thumbnailStyle = {
    width: `${youtubeWidth}px`,
    height: `${youtubeHeight}px`,
    aspectRatio: youtubeAspectRatio ? `1 / ${youtubeAspectRatio / 100}` : undefined,
  };

  if (YoutubeIsEmpty({ youtubeVideoId, id, isInEditor, cqPath })) {
    return null;
  }

  return (
    <>
      {preconnected && (
        <Helmet>
          <link rel="preconnect" href={YOUTUBE_URL} />
          <link rel="preconnect" href={GOOGLE_URL} />
        </Helmet>
      )}
      <article
        className={classNames(appliedCssClassNames, 'snowflake-youtube-lite', {
          'snowflake-youtube-lite-activated': iframe,
          'snowflake-youtube-lite-responsive': isResponsive,
        })}
        data-testid="snowflake-youtube-lite"
        data-title={accessibilityLabel}
        aria-label={!iframe ? accessibilityLabel : undefined}
        style={isResponsive && iframe ? { paddingTop: `${youtubeAspectRatio}%` } : {}}
        role={!iframe ? 'img' : undefined}
        onClick={addIframe}
        onMouseOver={warmConnections}
      >
        {!iframe && (
          <div className="snowflake-youtube-lite-thumbnail-wrapper">
            <img
              src={thumbnailUrl}
              alt={`${accessibilityLabel || t('youtube_video_thumbnail')}`}
              className="snowflake-youtube-lite-thumbnail"
              loading="lazy"
              style={thumbnailStyle}
            />
            <button
              type="button"
              className="snowflake-youtube-lite-playbtn"
              aria-label={`${accessibilityLabel || t('watch_video')}`}
              aria-hidden={iframe || undefined}
              tabIndex={0}
              onClick={addIframe}
            />
          </div>
        )}
        {iframe && (
          <YoutubeEmbed
            id={id}
            youtubeVideoId={youtubeVideoId}
            youtubeAspectRatio={youtubeAspectRatio}
            accessibilityLabel={accessibilityLabel}
            youtubeWidth={youtubeWidth}
            youtubeHeight={youtubeHeight}
            youtubeMute={youtubeMute}
            youtubeLoop={youtubeLoop}
            youtubeAutoPlay={true}
            layout={layout}
            isInEditor={isInEditor}
            cqPath={cqPath}
          />
        )}
      </article>
    </>
  );
};

export default Youtube;
